import { useMemo } from "react";

import { graphql } from "lib/generated/app-service-gql";
import { useGraphQlQuery } from "../useGraphQlClient";

const signalPreviewCountsQuery = graphql(`
  query signalPreviewCounts($request: SignalPreviewRequest!) {
    signalPreviewCounts(SignalPreviewRequest: $request) {
      counts {
        key
        count
      }
    }
  }
`);

// Category strings the backend understands. Defined here (not imported from the
// legacy StotlesApi) so this hook doesn't depend on the code being retired.
export const SIGNAL_PREVIEW_CATEGORY = {
  KEYWORD: "Keywords",
  COMPETITOR: "Competitors",
  PARTNER: "Partners",
  CPV_CODE: "CPV codes",
} as const;

export type SignalPreviewCategory =
  (typeof SIGNAL_PREVIEW_CATEGORY)[keyof typeof SIGNAL_PREVIEW_CATEGORY];

export type SignalPreviewItemInput = {
  // Stable key the count is returned under. Keep it equal to the value the
  // consuming input looks up by (keyword string, or org id for suppliers).
  key: string;
  category: SignalPreviewCategory;
  value: string;
};

type SignalPreviewOptions = {
  publishedWithinMonths?: number;
  countries?: string[];
};

/**
 * key -> match count. Keys with a null/absent count are omitted, so a missing
 * entry means "no reliable count" rather than "zero matches".
 */
export type SignalPreviewCountsByKey = Record<string, number>;

/**
 * Fetches per-signal feed-preview match counts from app-service. Each signal is
 * counted independently, so a slow or zero-match signal doesn't blank out the
 * rest, and counts are keyed by the caller's key.
 */
export function useSignalPreviewCounts(
  signals: SignalPreviewItemInput[],
  options?: SignalPreviewOptions,
) {
  const query = useGraphQlQuery(
    ["signalPreviewCounts", signals, options],
    signalPreviewCountsQuery,
    [{ request: { signals, options } }],
    { keepPreviousData: true, enabled: signals.length > 0, retry: false },
  );

  const countsByKey = useMemo<SignalPreviewCountsByKey>(() => {
    const map: SignalPreviewCountsByKey = {};
    for (const { key, count } of query.data?.signalPreviewCounts.counts ?? []) {
      if (typeof count === "number") {
        map[key] = count;
      }
    }
    return map;
  }, [query.data]);

  return { countsByKey, ...query };
}
